import { useState, useRef, useEffect } from "react";
import { MessageCircle, X, Send, Sparkles, CheckCircle } from "lucide-react";
import { Button } from "./ui/button";
import { Input } from "./ui/input";

const ChatAgent = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [isSubmitted, setIsSubmitted] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    message: ""
  });
  const inputRef = useRef(null);

  // Focus input when chat opens
  useEffect(() => {
    if (isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  }, [isOpen]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.message) return;
    
    setIsLoading(true);
    
    try {
      const response = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/contact`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          name: formData.name,
          email: formData.email,
          company: "",
          inquiryType: "Quick Message",
          message: formData.message
        })
      });

      if (response.ok) {
        setIsSubmitted(true);
        setFormData({ name: "", email: "", message: "" });
      }
    } catch (error) {
      console.error("Submit error:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleClose = () => {
    setIsOpen(false);
    // Reset after animation
    setTimeout(() => {
      setIsSubmitted(false);
    }, 300);
  };

  return (
    <>
      {/* Floating Chat Bubble */}
      <button
        onClick={() => setIsOpen(true)}
        className={`fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-swarm-violet hover:bg-swarm-violet/90 text-white shadow-lg shadow-swarm-violet/30 flex items-center justify-center transition-all duration-300 hover:scale-105 ${isOpen ? 'scale-0 opacity-0' : 'scale-100 opacity-100'}`}
        data-testid="chat-bubble"
        aria-label="Open contact form"
      >
        <MessageCircle className="w-6 h-6" />
        {/* Pulse animation */}
        <span className="absolute inset-0 rounded-full bg-swarm-violet animate-ping opacity-30" />
      </button>

      {/* Contact Widget */}
      <div
        className={`fixed bottom-6 right-6 z-50 w-[360px] bg-swarm-darker border border-swarm-border rounded-2xl shadow-2xl shadow-black/50 overflow-hidden transition-all duration-300 origin-bottom-right ${isOpen ? 'scale-100 opacity-100' : 'scale-0 opacity-0 pointer-events-none'}`}
        data-testid="chat-window"
      >
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-swarm-border bg-swarm-darker/80 backdrop-blur-sm">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-swarm-violet to-swarm-cyan flex items-center justify-center">
              <Sparkles className="w-5 h-5 text-white" />
            </div>
            <div>
              <h3 className="text-white font-semibold text-sm">Quick Contact</h3>
              <p className="text-slate-500 text-xs">We'll respond within 24 hours</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            className="w-8 h-8 rounded-lg hover:bg-white/5 flex items-center justify-center text-slate-400 hover:text-white transition-colors"
            data-testid="chat-close"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Content */}
        <div className="p-4">
          {isSubmitted ? (
            <div className="text-center py-8">
              <div className="w-16 h-16 rounded-full bg-swarm-green/10 flex items-center justify-center mx-auto mb-4">
                <CheckCircle className="w-8 h-8 text-swarm-green" />
              </div>
              <h4 className="text-white font-semibold mb-2">Message Sent!</h4>
              <p className="text-slate-400 text-sm mb-4">
                Thanks for reaching out. We'll get back to you soon.
              </p>
              <Button
                onClick={handleClose}
                variant="outline"
                className="btn-outline"
                size="sm"
              >
                Close
              </Button>
            </div>
          ) : (
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Input
                  ref={inputRef}
                  type="text"
                  placeholder="Your name"
                  value={formData.name}
                  onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                  className="bg-white/5 border-swarm-border text-white placeholder:text-slate-500"
                  required
                />
              </div>
              <div>
                <Input
                  type="email"
                  placeholder="Your email"
                  value={formData.email}
                  onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                  className="bg-white/5 border-swarm-border text-white placeholder:text-slate-500"
                  required
                />
              </div>
              <div>
                <textarea
                  placeholder="How can we help?"
                  value={formData.message}
                  onChange={(e) => setFormData(prev => ({ ...prev, message: e.target.value }))}
                  className="w-full bg-white/5 border border-swarm-border rounded-lg px-4 py-2.5 text-white text-sm placeholder:text-slate-500 focus:outline-none focus:border-swarm-violet/50 focus:ring-1 focus:ring-swarm-violet/20 resize-none"
                  rows={3}
                  required
                />
              </div>
              <Button
                type="submit"
                disabled={isLoading || !formData.name || !formData.email || !formData.message}
                className="w-full bg-swarm-violet hover:bg-swarm-violet/90 disabled:opacity-50"
              >
                {isLoading ? (
                  <span className="flex items-center gap-2">
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    Sending...
                  </span>
                ) : (
                  <span className="flex items-center gap-2">
                    <Send className="w-4 h-4" />
                    Send Message
                  </span>
                )}
              </Button>
            </form>
          )}
        </div>
      </div>
    </>
  );
};

export default ChatAgent;
